import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ProjectProvider } from "./contexts/ProjectContext";
import { ClientProjectProvider } from "./contexts/ClientProjectContext";
import { Toaster } from "./components/ui/sonner";
import { KeepAlive } from "./components/KeepAlive";

import Login from "./pages/Login";
import AdminLayout from "./pages/admin/AdminLayout";
import AdminDashboard from "./pages/admin/Dashboard";
import AdminProjects from "./pages/admin/Projects";
import AdminClients from "./pages/admin/Clients";
import AdminPromoters from "./pages/admin/Promoters";
import AdminStores from "./pages/admin/Stores";
import AdminSkus from "./pages/admin/Skus";
import AdminSurveyOptions from "./pages/admin/SurveyOptions";
import AdminReports from "./pages/admin/Reports";
import AdminShiftDetail from "./pages/admin/ShiftDetail";

import ClientLayout from "./pages/client/ClientLayout";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientAttendance from "./pages/client/ClientAttendance";
import ClientStock from "./pages/client/ClientStock";
import ClientSurveys from "./pages/client/ClientSurveys";

import PromoterLayout from "./pages/promoter/PromoterLayout";
import PromoterHome from "./pages/promoter/Home";
import PromoterCheckIn from "./pages/promoter/CheckIn";
import PromoterCheckOut from "./pages/promoter/CheckOut";
import PromoterStock from "./pages/promoter/Stock";
import PromoterSurvey from "./pages/promoter/Survey";
import PromoterSurveys from "./pages/promoter/Surveys";
import PromoterFAQ from "./pages/promoter/FAQ";
import AdminFAQEditor from "./pages/admin/FAQEditor";
import AdminLoginPageSettings from "./pages/admin/LoginPageSettings";

function Protected({ role, children }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="font-mono text-xs text-neutral-500 uppercase tracking-widest">
          Authenticating…
        </div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    const dest = user.role === "admin" ? "/admin" : user.role === "client" ? "/client" : "/promoter";
    return <Navigate to={dest} replace />;
  }
  return children;
}

function RootRedirect() {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (!user) return <Navigate to="/login" replace />;
  const dest = user.role === "admin" ? "/admin" : user.role === "client" ? "/client" : "/promoter";
  return <Navigate to={dest} replace />;
}

export default function App() {
  return (
    <div className="App">
      <AuthProvider>
        <KeepAlive />
        <BrowserRouter>
          <Routes>
            <Route path="/" element={<RootRedirect />} />
            <Route path="/login" element={<Login />} />

            <Route
              path="/admin"
              element={
                <Protected role="admin">
                  <ProjectProvider>
                    <AdminLayout />
                  </ProjectProvider>
                </Protected>
              }
            >
              <Route index element={<AdminDashboard />} />
              <Route path="projects" element={<AdminProjects />} />
              <Route path="clients" element={<AdminClients />} />
              <Route path="promoters" element={<AdminPromoters />} />
              <Route path="faq" element={<AdminFAQEditor />} />
              <Route path="login-page" element={<AdminLoginPageSettings />} />
              <Route path="stores" element={<AdminStores />} />
              <Route path="skus" element={<AdminSkus />} />
              <Route path="survey-options" element={<AdminSurveyOptions />} />
              <Route path="reports" element={<AdminReports />} />
              <Route path="shifts/:id" element={<AdminShiftDetail />} />
            </Route>

            <Route
              path="/client"
              element={
                <Protected role="client">
                  <ClientProjectProvider>
                    <ClientLayout />
                  </ClientProjectProvider>
                </Protected>
              }
            >
              <Route index element={<ClientDashboard />} />
              <Route path="attendance" element={<ClientAttendance />} />
              <Route path="stock" element={<ClientStock />} />
              <Route path="surveys" element={<ClientSurveys />} />
            </Route>

            <Route
              path="/promoter"
              element={
                <Protected role="promoter">
                  <PromoterLayout />
                </Protected>
              }
            >
              <Route index element={<PromoterHome />} />
              <Route path="check-in" element={<PromoterCheckIn />} />
              <Route path="check-out" element={<PromoterCheckOut />} />
              <Route path="stock" element={<PromoterStock />} />
              <Route path="survey" element={<PromoterSurvey />} />
              <Route path="surveys" element={<PromoterSurveys />} />
              <Route path="faq" element={<PromoterFAQ />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          <Toaster position="top-center" richColors />
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}
